import { useState, useEffect, useRef } from "react"
import { TrendingUp, TrendingDown, Activity, RefreshCw, Circle, DollarSign, BarChart2, AlertTriangle, Wifi, WifiOff } from "lucide-react"
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts"
import api from "../services/api"
import Badge from "../components/common/Badge"
import { formatCurrency, formatPct, formatDatetime } from "../utils/formatters"
import { PageLoader } from "../components/common/LoadingSpinner"

export default function Trading() {
  const [trades, setTrades] = useState([])
  const [activeBots, setActiveBots] = useState([])
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState("all")
  const [autoRefresh, setAutoRefresh] = useState(false)
  const intervalRef = useRef(null)

  async function loadData() {
    try {
      const [t, b] = await Promise.all([api.get("/trading/trades"), api.get("/trading/active-bots")])
      setTrades(t.data.items || t.data || [])
      setActiveBots(b.data || [])
    } catch {} finally { setLoading(false) }
  }

  useEffect(() => { loadData() }, [])
  useEffect(() => {
    if (autoRefresh) intervalRef.current = setInterval(loadData, 5000)
    else clearInterval(intervalRef.current)
    return () => clearInterval(intervalRef.current)
  }, [autoRefresh])

  const filtered = mode === "all" ? trades : trades.filter(t => t.mode === mode)
  const openTrades = trades.filter(t => t.status === "open")
  const totalPnl = trades.reduce((s, t) => s + (t.pnl_usd ?? 0), 0)
  const winCount = trades.filter(t => t.status === "closed" && (t.pnl_pct ?? 0) > 0).length
  const closedCount = trades.filter(t => t.status === "closed").length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Live Trading Monitor</h2>
          <div className="flex items-center gap-2 mt-1">
            <AlertTriangle size={12} className="text-trading-yellow"/>
            <p className="text-xs text-trading-yellow">For informational purposes only. Not financial advice.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAutoRefresh(r => !r)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${autoRefresh ? "bg-trading-green/15 text-trading-green border border-trading-green/30" : "btn-secondary"}`}>
            {autoRefresh ? <Wifi size={13}/> : <WifiOff size={13}/>}
            {autoRefresh ? "Live" : "Auto-refresh"}
          </button>
          <button onClick={loadData} className="btn-secondary flex items-center gap-2 text-xs"><RefreshCw size={13}/> Refresh</button>
        </div>
      </div>

      {/* P&L summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Open Positions", val: openTrades.length, color: "text-brand-400", sub: "active now" },
          { label: "Net P&L", val: formatCurrency(totalPnl), color: totalPnl >= 0 ? "text-trading-green" : "text-trading-red", sub: "all time" },
          { label: "Win Rate", val: closedCount > 0 ? `${((winCount/closedCount)*100).toFixed(0)}%` : "—", color: "text-white", sub: `${winCount}/${closedCount}` },
          { label: "Total Trades", val: trades.length, color: "text-white", sub: "all modes" },
        ].map(({ label, val, color, sub }) => (
          <div key={label} className="card-sm">
            <p className="text-xs text-gray-400 mb-1">{label}</p>
            <p className={`text-xl font-bold ${color}`}>{val}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* Active bots strip */}
      {activeBots.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Active Bots</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {activeBots.slice(0, 4).map(bot => (
              <div key={bot.id} className="card-sm border-trading-green/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 bg-trading-green rounded-full animate-pulse"/>
                  <span className="text-xs font-medium text-white truncate">{bot.name}</span>
                </div>
                <p className="text-[10px] text-gray-400 mb-1">{bot.symbol}</p>
                <p className={`text-sm font-mono font-semibold ${(bot.total_pnl_pct ?? 0) >= 0 ? "text-trading-green" : "text-trading-red"}`}>{formatPct(bot.total_pnl_pct)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open positions */}
      {openTrades.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Circle size={10} className="text-trading-green fill-trading-green"/>
            <h3 className="font-semibold text-white text-sm">Open Positions ({openTrades.length})</h3>
          </div>
          <div className="space-y-2">
            {openTrades.map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 bg-surface-100 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded-lg ${t.side === "buy" ? "bg-trading-green/10" : "bg-trading-red/10"}`}>
                    {t.side === "buy" ? <TrendingUp size={14} className="text-trading-green"/> : <TrendingDown size={14} className="text-trading-red"/>}
                  </div>
                  <div>
                    <p className="font-mono font-semibold text-white text-sm">{t.symbol}</p>
                    <p className="text-xs text-gray-400">Entry: {formatCurrency(t.entry_price)} · Qty: {t.quantity}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-mono font-bold text-sm ${(t.pnl_pct ?? 0) >= 0 ? "text-trading-green" : "text-trading-red"}`}>{t.pnl_pct != null ? formatPct(t.pnl_pct) : "—"}</p>
                  <Badge variant={t.mode === "live" ? "green" : "yellow"}>{t.mode}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trade history table */}
      <div className="card overflow-x-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-brand-400"/>
            <h3 className="font-semibold text-white">Trade History ({filtered.length})</h3>
          </div>
          <div className="flex gap-2">
            {["all","paper","live"].map(m => (
              <button key={m} onClick={() => setMode(m)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${mode === m ? "bg-brand-500 text-white" : "text-gray-400 hover:text-white bg-surface-100"}`}>{m}</button>
            ))}
          </div>
        </div>
        {loading ? <PageLoader/> : filtered.length === 0 ? (
          <div className="text-center py-12">
            <BarChart2 size={36} className="mx-auto text-gray-600 mb-2"/>
            <p className="text-gray-500 text-sm">No trades found</p>
            <p className="text-gray-600 text-xs mt-1">Start a bot to see trade history here</p>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="text-gray-500 text-xs border-b border-surface-200">
              {["Symbol","Side","Mode","Entry","Exit","Qty","P&L","Status","Date"].map(h => (
                <th key={h} className={`pb-2 font-medium ${h === "Symbol" || h === "Side" ? "text-left" : "text-right"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-surface-200">
              {filtered.map(t => (
                <tr key={t.id} className="hover:bg-surface-100 transition-colors">
                  <td className="py-2.5 font-mono font-medium text-white">{t.symbol}</td>
                  <td className="py-2.5">
                    <span className={`flex items-center gap-1 text-xs font-medium w-fit ${t.side === "buy" ? "text-trading-green" : "text-trading-red"}`}>
                      {t.side === "buy" ? <TrendingUp size={11}/> : <TrendingDown size={11}/>} {t.side?.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-2.5 text-right"><Badge variant={t.mode === "live" ? "green" : "yellow"}>{t.mode}</Badge></td>
                  <td className="py-2.5 text-right font-mono text-gray-300">{formatCurrency(t.entry_price)}</td>
                  <td className="py-2.5 text-right font-mono text-gray-400">{t.exit_price ? formatCurrency(t.exit_price) : "—"}</td>
                  <td className="py-2.5 text-right font-mono text-gray-400">{t.quantity}</td>
                  <td className={`py-2.5 text-right font-mono font-semibold ${(t.pnl_pct ?? 0) >= 0 ? "text-trading-green" : "text-trading-red"}`}>{t.pnl_pct != null ? formatPct(t.pnl_pct) : "—"}</td>
                  <td className="py-2.5 text-right"><Badge variant={t.status === "open" ? "blue" : t.status === "closed" ? "gray" : "red"}>{t.status}</Badge></td>
                  <td className="py-2.5 text-right text-gray-500 text-xs">{formatDatetime(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
